import React from 'react';
import { Job } from '@core/generated/gql-types';
import DaySequenceItem from '../daySequence/DaySequenceItem';
import UpdateTimesheetModal from './UpdateTimesheetModal';
import EditTimesheetModal from './EditTimesheetModal';
import CreateTimesheetModal from './CreateTimesheetModal';
import CheckoutTimesheetModal from './CheckoutTimesheetModal';
import { useJobTimesheets } from './useJobTimesheets';
import { usePortalAccess } from '@/hooks';
import Button from '@/components/Button';

interface JobTimesheetsProps {
  job: Job;
}

const JobTimesheets: React.FC<JobTimesheetsProps> = ({ job }) => {
  const { isClientPortal } = usePortalAccess();
  const {
    isLoading,
    sortedDaySequences,
    canCreateTimesheet,
    isUpdateTimesheetConfirmationOpen,
    isEditTimesheetOpen,
    isCreateTimesheetOpen,
    isCheckoutTimesheetOpen,
    setIsCreateTimesheetOpen,
    updateTimesheet,
    editAction,
    handleApproveTimesheet,
    handleResolveTimesheetDispute,
    handleProposeTimesheetChanges,
    handleCreateTimesheet,
    handleCheckoutTimesheet,
    openUpdateTimesheetConfirmation,
    openEditTimesheetModal,
    openCheckoutTimesheetModal,
    onCloseUpdateTimesheetConfirmation,
    onCloseEditTimesheet,
    onCloseCreateTimesheet,
    onCloseCheckoutTimesheet,
  } = useJobTimesheets(job, isClientPortal);

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold">Timesheets</h2>
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center">Loading...</div>
        ) : (
          <>
            <UpdateTimesheetModal
              isOpen={isUpdateTimesheetConfirmationOpen}
              onClose={onCloseUpdateTimesheetConfirmation}
              updateTimesheet={updateTimesheet || null}
              finalClockInTime={updateTimesheet?.clockInTime || ''}
              finalClockOutTime={updateTimesheet?.clockOutTime || ''}
              finalBreakTime={updateTimesheet?.breaks || null}
              confirmationVariant="approve"
              onApproveTimesheet={handleApproveTimesheet}
            />

            <EditTimesheetModal
              isOpen={isEditTimesheetOpen}
              onClose={onCloseEditTimesheet}
              job={job}
              updateTimesheet={updateTimesheet || null}
              editAction={editAction}
              onResolveTimesheetDispute={handleResolveTimesheetDispute}
              onProposeTimesheetChanges={handleProposeTimesheetChanges}
            />

            <CheckoutTimesheetModal
              isOpen={isCheckoutTimesheetOpen}
              onClose={onCloseCheckoutTimesheet}
              job={job}
              daySequence={updateTimesheet}
              onCheckout={handleCheckoutTimesheet}
            />

            <CreateTimesheetModal
              isOpen={isCreateTimesheetOpen}
              onClose={onCloseCreateTimesheet}
              job={job}
              handleCreateTimesheet={handleCreateTimesheet}
            />
            {sortedDaySequences.length > 0 ? (
              <div className="space-y-4">
                {sortedDaySequences.map((daySequence) => (
                  <DaySequenceItem
                    key={daySequence.id}
                    daySequence={daySequence}
                    isClientPortal={isClientPortal}
                    openEditTimesheetModal={openEditTimesheetModal}
                    openUpdateTimesheetConfirmation={
                      openUpdateTimesheetConfirmation
                    }
                    openCheckoutTimesheetModal={openCheckoutTimesheetModal}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm text-gray-500">No recent activity</p>
              </div>
            )}
            {canCreateTimesheet && (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsCreateTimesheetOpen(true)}
                className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
              >
                Create timesheet
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JobTimesheets;
